import React, { useState } from 'react';
import { InlineLoading } from 'carbon-components-react';

interface ImagePreviewProps {
  imageUrl: string;
  containerWidth?: number;
  containerHeight?: number;
  className?: string;
  style?: React.CSSProperties;
  isLoading?: boolean;
  onImageLoadSuccess?: () => void;
  onImageLoadError?: (error: any) => void;
}

const ImagePreview: React.FC<ImagePreviewProps> = React.memo(
  ({
    imageUrl,
    containerWidth = 188,
    containerHeight = 238,
    className = '',
    style = {},
    isLoading = false,
    onImageLoadSuccess,
    onImageLoadError
  }) => {
    const [hasError, setHasError] = useState<boolean>(false);
    const [imageOrientation, setImageOrientation] = useState<
      'landscape' | 'portrait' | 'square' | null
    >(null);

    // Reset orientation when URL changes
    React.useEffect(() => {
      setImageOrientation(null);
      setHasError(false);
    }, [imageUrl]);

    const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
      const img = event.currentTarget;
      const { naturalWidth, naturalHeight } = img;

      // Determine image orientation
      let orientation: 'landscape' | 'portrait' | 'square';
      if (naturalWidth > naturalHeight) {
        orientation = 'landscape';
      } else if (naturalHeight > naturalWidth) {
        orientation = 'portrait';
      } else {
        orientation = 'square';
      }

      setImageOrientation(orientation);
      setHasError(false);
      onImageLoadSuccess?.();
    };

    const handleImageError = (
      error: React.SyntheticEvent<HTMLImageElement>
    ) => {
      setHasError(true);
      onImageLoadError?.(error);
    };

    // Get the appropriate objectFit value based on image orientation
    // - Landscape: 'contain' shows full image within container (may have letterboxing)
    // - Portrait: 'fill' stretches to fill container completely
    // - Square: 'cover' fills container while maintaining aspect ratio
    const getObjectFit = (): 'contain' | 'cover' | 'fill' => {
      if (!imageOrientation) return 'cover'; // Default while loading

      switch (imageOrientation) {
        case 'landscape':
          return 'contain'; // Show full landscape image
        case 'portrait':
          return 'fill'; // Fill container for portrait
        case 'square':
          return 'cover'; // Cover for square images
        default:
          return 'cover';
      }
    };

    const LoadingComponent = () => (
      <div
        className="image-preview-loading"
        style={{
          width: containerWidth,
          height: containerHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <InlineLoading
          description="Loading preview..."
          style={{ justifyContent: 'center' }}
        />
      </div>
    );

    const ErrorComponent = () => (
      <div
        className="image-preview-error"
        style={{
          width: containerWidth,
          height: containerHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f2f4f8',
          color: '#878d96',
          fontSize: '12px',
          textAlign: 'center',
          border: '6px solid #ffffff',
          borderRadius: '8px'
        }}
      >
        Unable to load preview
      </div>
    );

    if (hasError) {
      return <ErrorComponent />;
    }

    // Show external loading if needed
    if (!imageUrl || isLoading) {
      return (
        <div
          className={`image-preview-container ${className}`}
          style={{
            width: containerWidth,
            height: containerHeight,
            ...style
          }}
        >
          <LoadingComponent />
        </div>
      );
    }

    // Display the image directly from backend
    return (
      <div
        className={`image-preview-container ${className}`}
        style={{
          width: containerWidth,
          height: containerHeight,
          overflow: 'hidden',
          borderRadius: '6px',
          ...style
        }}
      >
        <img
          src={imageUrl}
          alt="Thumbnail"
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            width: '100%',
            height: '100%',
            objectFit: getObjectFit(),
            display: 'block',
            border: '6px solid #ffffff',
            borderRadius: '8px'
          }}
        />
      </div>
    );
  }
);

ImagePreview.displayName = 'ImagePreview';

export default ImagePreview;
